/**
 * Reskin primitives shared by the rebuilt pages (Dashboard, Orders).
 *
 * These are the prototype's small vocabulary — the tracked mono label, the
 * band that separates a page's sections, the live dot, the KPI tile with its
 * glyph square — lifted out of Dashboard.tsx so the Orders page can use the
 * same pieces instead of re-drawing them.
 */

import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { useAdminTheme } from '../../admin-theme';
import { FONT_DATA, FONT_DISPLAY } from '../../theme/grounds';

/** Mono, tracked, uppercase — the prototype's `.band`, `.kpi .lab`, `.live-row .nm`. */
export const monoLabelSx = {
  fontFamily: FONT_DATA,
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
} as const;

/** Expanding ring behind the band's live dot — the prototype's `ring`. */
export const liveRing = keyframes`
  0%   { transform: scale(.5); opacity: .7; }
  100% { transform: scale(1.5); opacity: 0; }
`;

/** The green dot with its expanding ring — "this section is live". */
export const LiveDot: React.FC = () => {
  const { colors } = useAdminTheme();
  return (
    <Box sx={{ position: 'relative', width: 7, height: 7, flex: 'none' }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: colors.success }} />
      <Box
        sx={{
          position: 'absolute',
          inset: -4,
          borderRadius: '50%',
          border: `1px solid ${colors.success}`,
          opacity: 0.5,
          animation: `${liveRing} 2.4s ease-out infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
    </Box>
  );
};

/** A band label separating a page's sections. */
export const Band: React.FC<{ children: React.ReactNode; note?: React.ReactNode; live?: boolean; first?: boolean }> = ({
  children, note, live, first,
}) => {
  const { colors } = useAdminTheme();
  return (
    <Box
      sx={{
        ...monoLabelSx,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        mt: first ? 3.25 : 3.25,
        mb: 1.6,
        fontSize: '0.72rem',
        letterSpacing: '0.16em',
        color: colors.textSecondary,
      }}
    >
      {live && <LiveDot />}
      {children}
      {note && (
        <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: 400, letterSpacing: 0, textTransform: 'none', color: colors.textMuted }}>
          {note}
        </Typography>
      )}
    </Box>
  );
};

/**
 * A stroke glyph in the prototype's 24-box, 1.9px, no-fill style. `paths` is
 * the inner SVG markup (rects, paths, circles) — no wrapping <svg>.
 */
export const StrokeGlyph: React.FC<{ paths: string; size?: number }> = ({ paths, size = 19 }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.9}
    strokeLinecap="round"
    strokeLinejoin="round"
    sx={{ width: size, height: size, display: 'block' }}
    dangerouslySetInnerHTML={{ __html: paths }}
  />
);

/**
 * KPI tile — the prototype's `.kpi` with a `.tile` glyph square, a tracked
 * label and a big display number. Used in a 4-up grid under a "Right now" band.
 */
export const KpiTile: React.FC<{ glyph: string; label: string; value: number | string }> = ({ glyph, label, value }) => {
  const { colors } = useAdminTheme();
  return (
    <Box
      sx={{
        bgcolor: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 'var(--r-panel, 12px)',
        p: '13px 15px 15px',
        display: 'flex',
        alignItems: 'center',
        gap: '13px',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 42, height: 42, borderRadius: '9px', flex: 'none',
          display: 'grid', placeItems: 'center',
          bgcolor: 'var(--ember-bg)', border: '1px solid var(--ember-line)', color: 'var(--ember)',
        }}
      >
        <StrokeGlyph paths={glyph} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ ...monoLabelSx, fontSize: '0.66rem', color: colors.textMuted, whiteSpace: 'nowrap' }}>{label}</Box>
        <Box
          sx={{
            fontFamily: FONT_DISPLAY, fontStretch: '125%', fontWeight: 800, fontSize: '2.1rem',
            lineHeight: 0.95, letterSpacing: '-0.04em', color: colors.textPrimary,
            fontVariantNumeric: 'tabular-nums', mt: '4px',
          }}
        >
          {value}
        </Box>
      </Box>
    </Box>
  );
};
